import { useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { logError } from '@/lib/logger';

export const useCapacitor = () => {
  const [isNative, setIsNative] = useState(false);
  const [platform, setPlatform] = useState('web');

  useEffect(() => {
    const initCapacitor = async () => {
      const native = Capacitor.isNativePlatform();
      setIsNative(native);
      setPlatform(Capacitor.getPlatform());

      if (native) {
        // Configure status bar for dark theme
        try {
          await StatusBar.setStyle({ style: Style.Dark });
          await StatusBar.setBackgroundColor({ color: '#FBF9F6' });
        } catch (e) {
          logError('StatusBar not available');
        }

        // Hide splash screen after app is ready
        try {
          await SplashScreen.hide();
        } catch (e) {
          logError('SplashScreen not available');
        }

        // Handle app URL open (deep links)
        App.addListener('appUrlOpen', (event) => {
          const url = event.url;
          // Handle deep links here
          
          // Example: handle /sanctuary deep link
          if (url.includes('/sanctuary')) {
            window.location.href = '/sanctuary';
          } else if (url.includes('/circle')) {
            window.location.href = '/circle';
          } else if (url.includes('/chat')) {
            window.location.href = '/chat';
          }
        });

        // Handle app state changes
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            // App came to foreground - refresh data if needed
            window.dispatchEvent(new CustomEvent('app-foreground'));
          }
        });

        // Handle back button on Android
        App.addListener('backButton', ({ canGoBack }) => {
          if (!canGoBack) {
            App.exitApp();
          } else {
            window.history.back();
          }
        });
      }
    };

    initCapacitor();

    return () => {
      if (Capacitor.isNativePlatform()) {
        App.removeAllListeners();
      }
    };
  }, []);

  return { isNative, platform };
};

// Hook for Firebase Cloud Messaging (Android) and APNs (iOS)
export const useFirebasePushNotifications = (token) => {
  const [fcmToken, setFcmToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [isLoading, setIsLoading] = useState(false);

  const registerForPush = useCallback(async () => {
    if (!Capacitor.isNativePlatform() || !token) return null;

    setIsLoading(true);
    
    try {
      // Dynamically import Firebase Messaging to avoid issues on web
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      
      // Check current permission status
      const permStatus = await FirebaseMessaging.checkPermissions();
      
      if (permStatus.receive !== 'granted') {
        // Request permission
        const requestResult = await FirebaseMessaging.requestPermissions();
        setPermissionStatus(requestResult.receive);
        
        if (requestResult.receive !== 'granted') {
          logError('Push notification permission denied');
          setIsLoading(false);
          return null;
        }
      } else {
        setPermissionStatus('granted');
      }

      // Get FCM token
      const { token: pushToken } = await FirebaseMessaging.getToken();
      setFcmToken(pushToken);

      // Send token to backend
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/push/native-subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          token: pushToken,
          platform: Capacitor.getPlatform()
        })
      });

      // Listen for token refresh
      FirebaseMessaging.addListener('tokenReceived', async (event) => {
        setFcmToken(event.token);
        
        // Update backend with new token
        await fetch(`${API}/api/push/native-subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            token: event.token,
            platform: Capacitor.getPlatform()
          })
        });
      });

      // Listen for foreground notifications
      FirebaseMessaging.addListener('notificationReceived', (notification) => {
        window.dispatchEvent(new CustomEvent('push-received', { detail: notification }));
      });

      // Listen for notification tap actions
      FirebaseMessaging.addListener('notificationActionPerformed', (notification) => {
        const data = notification.notification?.data;
        if (data?.url) {
          window.location.href = data.url;
        } else if (data?.type === 'circle_reply') {
          window.location.href = '/circle';
        } else if (data?.type === 'daily_prompt') {
          window.location.href = '/sanctuary';
        }
      });

      setIsLoading(false);
      return pushToken;

    } catch (error) {
      logError('Failed to initialize Firebase push notifications:', error);
      setIsLoading(false);
      return null;
    }
  }, [token]);

  const unregisterFromPush = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      await FirebaseMessaging.deleteToken();
      setFcmToken(null);
      
      // Remove listeners
      FirebaseMessaging.removeAllListeners();
      
    } catch (error) {
      logError('Failed to unregister from push:', error);
    }
  }, []);

  useEffect(() => {
    if (token && Capacitor.isNativePlatform()) {
      registerForPush();
    }

    return () => {
      if (Capacitor.isNativePlatform()) {
        import('@capacitor-firebase/messaging').then(({ FirebaseMessaging }) => {
          FirebaseMessaging.removeAllListeners();
        }).catch(() => {});
      }
    };
  }, [token, registerForPush]);

  return { 
    fcmToken, 
    permissionStatus, 
    isLoading, 
    registerForPush, 
    unregisterFromPush 
  };
};

export default useCapacitor;
